import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { App } from '@capacitor/app';
import { v4 as uuidv4 } from 'uuid';

const addAppListener = (
  eventName: string,
  listenerFunc: (...args: any[]) => void
): Promise<PluginListenerHandle | undefined> => {
  switch (eventName) {
    case 'appStateChange':
      return App.addListener('appStateChange', listenerFunc as (state: { isActive: boolean }) => void);

    case 'pause':
      return App.addListener('pause', listenerFunc as () => void);

    case 'resume':
      return App.addListener('resume', listenerFunc as () => void);

    case 'appUrlOpen':
      return App.addListener('appUrlOpen', listenerFunc as (data: { url: string }) => void);

    case 'appRestoredResult':
      return App.addListener('appRestoredResult', listenerFunc as (result: unknown) => void);

    case 'backButton':
      return App.addListener('backButton', listenerFunc as () => void);

    default:
      console.error(`Event name "${eventName}" is not supported.`);
      return Promise.resolve(undefined);
  }
};
export type AppEventId = string | undefined;
type PlatformType = 'web' | 'android' | 'ios' | null;

type Listener = {
	id: string; 
  eventName: string;
  callback: (...args: any[]) => void;
  handle?: PluginListenerHandle;
	isActive: boolean; 
	exclusive: boolean;
  priority: number;
	platform?: PlatformType
};

class AppEventManager {
  private listeners: Listener[] = [];

	private updateListenersActivation(eventName: string): void {
    const listenersForEvent = this.listeners.filter(
      (listener) => listener.eventName === eventName
    );

    const exclusiveListeners = listenersForEvent.filter((listener) => listener.exclusive);

    if (exclusiveListeners.length > 0) {
      // Trouver la priorité maximale
			const maxPriority = Math.max(...exclusiveListeners.map((listener) => listener.priority));

			// Filtrer les exclusifs avec la priorité maximale
			const highestPriorityListeners = exclusiveListeners.filter(
				(listener) => listener.priority === maxPriority
			);

			// Activer uniquement le dernier ajouté (par ordre dans la liste)
			const listenerToActivate = highestPriorityListeners[highestPriorityListeners.length-1];
			listenersForEvent.forEach((listener) => {
				listener.isActive = listener === listenerToActivate;
			});
    } else {
      // Activer tous les listeners si aucun exclusif n'existe
      listenersForEvent.forEach((listener) => {
        listener.isActive = true;
      });
    }
  }

	async addListener(
    eventName: string,
    callback: (...args: any[]) => void,
    options?: { exclusive?: boolean; priority?: number, platform?: PlatformType }
  ): Promise<AppEventId> {
		const platform = options?.platform ?? null;

		if(platform && Capacitor.getPlatform() != platform) return;

		const isExclusive = !!options?.exclusive;
		const priority = options?.priority ?? 1;

    // Générer un identifiant unique pour ce listener
    const id = uuidv4();

		// Ajouter le nouveau listener
    const handle = await addAppListener(eventName, (...args) => {
      const listener = this.listeners.find((l) => l.id === id);
      if (listener?.isActive) {
        callback(...args);
      }
    });

    this.listeners.push({
      id,
      eventName,
      callback,
      handle,
      isActive: true, // Initialement actif
      exclusive: isExclusive,
      priority,
			platform,
    });

    // Mise à jour des activations après ajout
    this.updateListenersActivation(eventName);

		return id;
  }
	
  removeListenerById(id: AppEventId): void {
    const listenerIndex = this.listeners.findIndex((listener) => listener.id === id);

    if (listenerIndex !== -1) {
      const [removedListener] = this.listeners.splice(listenerIndex, 1);
      removedListener.handle?.remove();

      // Mise à jour des activations après suppression
      this.updateListenersActivation(removedListener.eventName);
    }
  }

  clearAllListeners() {
		// if (!Capacitor.isNativePlatform()) return;
    this.listeners.forEach((listener) => listener.handle?.remove());
    this.listeners = [];
  }
}

export const useAppEventManager = new AppEventManager();